/*
 * The React binding. Deliberately separate from the core, which is framework-free — the same
 * split CLAUDE.md requires of domain logic ("plain services, NestJS only at the edges"), for the
 * same reason: the catalogue and the formatters are testable without a renderer.
 */

import { createContext, createElement, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { createTranslator, resolveLocale } from './index.js';
import type { LocalePreferences, Translator } from './index.js';

const I18nContext = createContext<Translator | null>(null);

export interface I18nProviderProps extends LocalePreferences {
  children: ReactNode;
}

export function I18nProvider({ user, tenant, children }: I18nProviderProps) {
  const translator = useMemo(() => createTranslator(resolveLocale({ user, tenant })), [user, tenant]);

  return createElement(I18nContext.Provider, { value: translator }, children);
}

/**
 * Throws rather than falling back to a default translator.
 *
 * A silent fallback would render English inside an app that thought it had mounted a provider,
 * and the bug would surface as "translations do not apply" in whichever locale ships second.
 */
export function useI18n(): Translator {
  const translator = useContext(I18nContext);
  if (!translator) {
    throw new Error('useI18n was called outside an <I18nProvider>. Mount one at the app root.');
  }
  return translator;
}

/** The common case: `const t = useT()`. */
export function useT(): Translator['t'] {
  return useI18n().t;
}
